import { useEffect, useRef, useState, useCallback } from 'react'
import { Loader2, MapPin } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { api } from '@/lib/api'

interface PlaceSelectedPayload {
  address1: string
  city: string
  province: string
  postalCode: string
}

interface Props {
  value: string
  onChange: (value: string) => void
  /**
   * Si fourni : mode champs séparés — l'autocomplete remplit l'input avec
   * address1 uniquement et appelle ce callback avec le reste (city,
   * province, postalCode). Sinon : mode champ unique (adresse formatée
   * complète injectée via onChange).
   */
  onPlaceSelected?: (place: PlaceSelectedPayload) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  ariaInvalid?: boolean
  id?: string
}

interface Suggestion {
  description: string
  place_id: string
}

interface AddressComponent {
  long_name: string
  short_name: string
  types: string[]
}

const PROVINCE_MAP: Record<string, string> = {
  AB: 'Alberta',
  BC: 'British Columbia',
  MB: 'Manitoba',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  NS: 'Nova Scotia',
  ON: 'Ontario',
  PE: 'Prince Edward Island',
  QC: 'Quebec',
  SK: 'Saskatchewan',
}

const MIN_QUERY_LENGTH = 3
const DEBOUNCE_MS = 500

export function GoogleAddressAutocomplete({
  value,
  onChange,
  onPlaceSelected,
  placeholder,
  disabled = false,
  className = '',
  ariaInvalid,
  id,
}: Props) {
  const { t } = useTranslation()
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  // Empêche de relancer la recherche pendant qu'on remplit l'input depuis une suggestion choisie.
  const isSelectingRef = useRef(false)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const fetchSuggestions = useCallback(
    async (input: string) => {
      if (input.length < MIN_QUERY_LENGTH) {
        setSuggestions([])
        setShowDropdown(false)
        return
      }

      setIsLoading(true)
      setError(null)
      try {
        const { data } = await api.get<{
          predictions?: Suggestion[]
          status?: string
          error_message?: string
        }>('/api/places/autocomplete/', { params: { input } })

        if (data.status && data.status !== 'OK' && data.status !== 'ZERO_RESULTS') {
          throw new Error(data.error_message || `Google API error: ${data.status}`)
        }

        const next = data.predictions ?? []
        setSuggestions(next)
        setShowDropdown(next.length > 0)
      } catch (err) {
        console.error('[GoogleAddressAutocomplete] autocomplete failed', err)
        setError(t('googleAddressAutocomplete.error'))
        setSuggestions([])
        setShowDropdown(false)
      } finally {
        setIsLoading(false)
      }
    },
    [t],
  )

  const handleInputChange = (newValue: string) => {
    onChange(newValue)
    if (isSelectingRef.current) return

    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      void fetchSuggestions(newValue)
    }, DEBOUNCE_MS)
  }

  const handleSuggestionClick = async (suggestion: Suggestion) => {
    isSelectingRef.current = true
    setShowDropdown(false)
    onChange(suggestion.description)
    setIsLoading(true)

    try {
      const { data } = await api.get<{
        result?: { address_components?: AddressComponent[] }
      }>('/api/places/details/', { params: { placeId: suggestion.place_id } })

      const components = data.result?.address_components ?? []
      let streetNumber = ''
      let route = ''
      let city = ''
      let province = ''
      let postalCode = ''

      components.forEach((component) => {
        const types = component.types
        if (types.includes('street_number')) streetNumber = component.long_name
        if (types.includes('route')) route = component.long_name
        if (types.includes('locality')) city = component.long_name
        if (types.includes('administrative_area_level_1')) {
          province = PROVINCE_MAP[component.short_name] || component.long_name
        }
        if (types.includes('postal_code')) postalCode = component.long_name
      })

      const address1 = `${streetNumber} ${route}`.trim()

      if (onPlaceSelected) {
        onChange(address1)
        onPlaceSelected({ address1, city, province, postalCode })
      } else {
        const parts = [address1, city, province, postalCode].filter(Boolean)
        onChange(parts.join(', '))
      }
    } catch (err) {
      console.error('[GoogleAddressAutocomplete] details failed', err)
      setError(t('googleAddressAutocomplete.detailsError'))
    } finally {
      setIsLoading(false)
      // Délai court pour laisser l'onChange final se propager avant de réactiver la recherche.
      setTimeout(() => {
        isSelectingRef.current = false
      }, 100)
    }
  }

  return (
    <div className="relative" ref={wrapperRef}>
      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 z-10 pointer-events-none" />
      <Input
        id={id}
        type="text"
        placeholder={placeholder ?? t('googleAddressAutocomplete.placeholder')}
        value={value}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => {
          if (suggestions.length > 0) setShowDropdown(true)
        }}
        disabled={disabled}
        aria-invalid={ariaInvalid}
        className={`h-11 pl-10 ${className}`}
        autoComplete="off"
      />
      {isLoading && (
        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400 z-10" />
      )}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
      {showDropdown && suggestions.length > 0 && (
        <div className="ph-mask absolute z-50 w-full mt-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.place_id}
              type="button"
              className="w-full text-left px-4 py-3 hover:bg-blue-50 dark:hover:bg-slate-800 transition-colors border-b border-slate-100 dark:border-slate-700 last:border-b-0 flex items-start gap-3"
              onClick={() => void handleSuggestionClick(suggestion)}
            >
              <MapPin className="h-4 w-4 text-slate-400 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-slate-700 dark:text-slate-200">
                {suggestion.description}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
